import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import { MessageReactions } from './MessageReactions'
import { CHAT_REACTION_EMOJIS } from '@/api/chat'
import type { ChatAttachment, ChatMessage } from '@/api/chat'
import { storagePublicUrl } from '@/utils/storage'

/** Зеркало config('chat.edit_window_minutes') backend — 1 день. */
const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000

interface MessageBubbleProps {
  message: ChatMessage
  isOwn: boolean
  showAuthor: boolean
  canWrite: boolean
  currentUserId: number
  onEdit: (message: ChatMessage) => void
  onDelete: (message: ChatMessage) => void
  onCopyText: (message: ChatMessage) => void
  onToggleReaction: (messageId: number, emoji: string) => void
}

/**
 * Пузырь сообщения ленты чата. Все действия — в контекстном меню по правому
 * клику (как в привычных мессенджерах): ряд реакций + «Редактировать»/«Удалить»
 * для своих сообщений. memo — реакции приходят realtime-патчами, без memo
 * каждое обновление перерисовывало бы всю ленту.
 */
export const MessageBubble = memo(function MessageBubble({
  message,
  isOwn,
  showAuthor,
  canWrite,
  currentUserId,
  onEdit,
  onDelete,
  onCopyText,
  onToggleReaction,
}: MessageBubbleProps) {
  const { t } = useTranslation()
  const [contextMenu, setContextMenu] = useState<{ top: number; left: number } | null>(null)
  const menuPaperRef = useRef<HTMLDivElement | null>(null)

  // Меню немодальное (см. props Menu ниже), поэтому закрытие «по клику
  // снаружи» — своё: mousedown ловит и левую, и правую кнопку (contextmenu
  // соседнего сообщения придёт следом и откроет уже его меню), скролл ленты
  // закрывает меню как в нативных.
  useEffect(() => {
    if (contextMenu === null) return
    const handleOutside = (event: Event) => {
      if (menuPaperRef.current?.contains(event.target as Node)) return
      setContextMenu(null)
    }
    const handleScroll = () => setContextMenu(null)
    document.addEventListener('mousedown', handleOutside, true)
    document.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handleOutside, true)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [contextMenu])

  const authorAvatar = message.author?.avatar_url ?? undefined
  const isStickerMessage = message.attachments.some((a) => a.type === 'sticker')
  // Сообщение-стикер рендерится «голым», без пузыря (как в Telegram);
  // удалённое показывает обычный пузырь с плейсхолдером.
  const isBareSticker = isStickerMessage && !message.text && !message.is_deleted
  const isWithinEditWindow =
    message.created_at !== null &&
    Date.now() - new Date(message.created_at).getTime() < EDIT_WINDOW_MS
  const canEdit =
    isOwn && canWrite && !message.is_deleted && !isStickerMessage && isWithinEditWindow
  const canDelete = isOwn && canWrite && !message.is_deleted
  // Копирование доступно и в read-only комнате; копировать нечего, когда
  // сообщение состоит только из вложения или стикера.
  const canCopy = !message.is_deleted && Boolean(message.text?.trim())

  const handleContextMenu = (event: React.MouseEvent) => {
    if (message.is_deleted) return
    event.preventDefault()
    setContextMenu({ top: event.clientY, left: event.clientX })
  }

  const closeMenu = () => setContextMenu(null)

  // Touch-путь к тем же действиям: iOS/iPad не шлют contextmenu, поэтому
  // рядом с пузырём есть кнопка — скрыта до hover на десктопе и всегда
  // видима на устройствах без hover (coarse pointer).
  const actionsButton = !message.is_deleted && (
    <IconButton
      className="message-actions"
      size="small"
      aria-label={t('chat.messageActions')}
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setContextMenu({ top: rect.bottom + 4, left: rect.left })
      }}
      sx={{
        alignSelf: 'center',
        opacity: 0,
        transition: 'opacity 0.15s',
        '@media (hover: none)': { opacity: 0.6 },
      }}
    >
      <MoreVertIcon fontSize="small" />
    </IconButton>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 1,
        gap: 0.5,
        '&:hover .message-actions': { opacity: 1 },
      }}
    >
      {!isOwn && (
        <Avatar src={authorAvatar} sx={{ width: 32, height: 32, mt: 0.25, mr: 0.5 }}>
          {message.author?.full_name?.[0] ?? '?'}
        </Avatar>
      )}
      {isOwn && actionsButton}
      <Box
        onContextMenu={handleContextMenu}
        sx={
          isBareSticker
            ? { maxWidth: '75%' }
            : {
                maxWidth: '75%',
                bgcolor: isOwn ? 'primary.main' : 'action.hover',
                color: isOwn ? 'primary.contrastText' : 'text.primary',
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }
        }
      >
        {showAuthor && !isOwn && message.author && (
          <Typography variant="caption" fontWeight={600} display="block">
            {message.author.full_name}
          </Typography>
        )}
        {message.is_deleted ? (
          <Typography variant="body2" fontStyle="italic">
            {t('chat.messageDeleted')}
          </Typography>
        ) : (
          <>
            {message.attachments.map((a, index) => (
              <Box key={`${a.type}-${index}-${a.path}`} sx={{ mb: message.text ? 0.5 : 0 }}>
                <AttachmentContent attachment={a} isOwn={isOwn} />
              </Box>
            ))}
            {message.text && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.text}
              </Typography>
            )}
          </>
        )}
        {!message.is_deleted && (
          <MessageReactions
            reactions={message.reactions ?? []}
            currentUserId={currentUserId}
            isOwn={isOwn}
            onBareBackground={isBareSticker}
            onToggle={(emoji) => onToggleReaction(message.id, emoji)}
          />
        )}
        <Typography
          variant="caption"
          sx={{
            opacity: 0.7,
            display: 'block',
            mt: 0.25,
            // По стороне сообщения: свои — справа, собеседника — слева
            textAlign: isOwn ? 'right' : 'left',
            ...(isBareSticker && { color: 'text.secondary' }),
          }}
        >
          {formatTime(message.created_at)}
          {message.edited_at ? ` · ${t('chat.edited')}` : ''}
        </Typography>
      </Box>
      {!isOwn && actionsButton}

      <Menu
        open={contextMenu !== null}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ?? undefined}
        disableAutoFocusItem
        // Немодальный режим: без backdrop и с «прозрачным» для мыши корнем,
        // иначе правый клик по соседнему сообщению попадал бы в backdrop —
        // наше меню не переоткрывалось, а браузер показывал своё.
        hideBackdrop
        disableScrollLock
        sx={{ pointerEvents: 'none' }}
        slotProps={{ paper: { ref: menuPaperRef, sx: { pointerEvents: 'auto' } } }}
      >
        <Box sx={{ display: 'flex', gap: 0.25, px: 1, pb: 0.5 }}>
          {CHAT_REACTION_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              sx={{ fontSize: '1.25rem' }}
              onClick={() => {
                closeMenu()
                onToggleReaction(message.id, emoji)
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
        {canCopy && (
          <MenuItem
            onClick={() => {
              closeMenu()
              onCopyText(message)
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('chat.copyMessage')}</ListItemText>
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              closeMenu()
              onEdit(message)
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('chat.editMessage')}</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              closeMenu()
              onDelete(message)
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('chat.deleteMessage')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
})

function AttachmentContent({ attachment, isOwn }: { attachment: ChatAttachment; isOwn: boolean }) {
  const { t } = useTranslation()

  if (attachment.type === 'sticker') {
    if (!attachment.path) {
      return (
        <Typography variant="body2" fontStyle="italic">
          {t('chat.stickerUnavailable')}
        </Typography>
      )
    }
    return (
      <img
        src={storagePublicUrl(attachment.path)}
        alt={t('chat.stickerPreview')}
        style={{ width: 140, height: 140, objectFit: 'contain', display: 'block' }}
      />
    )
  }

  if (attachment.type === 'image') {
    return (
      <img
        src={storagePublicUrl(attachment.path)}
        alt={attachment.name}
        style={{ maxWidth: '100%', borderRadius: 6, display: 'block' }}
      />
    )
  }

  return (
    <Chip
      component="a"
      href={storagePublicUrl(attachment.path)}
      target="_blank"
      rel="noopener noreferrer"
      clickable
      icon={<FileIcon />}
      label={`${attachment.name} · ${t('chat.download')}`}
      size="small"
      sx={{
        bgcolor: isOwn ? 'primary.dark' : 'background.paper',
        color: 'inherit',
        mb: 0.5,
      }}
    />
  )
}

function formatTime(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}
